import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "react-hot-toast";

import { Button } from "@shared/components/ui/Button";
import { User } from "@shared/types/user";
import theme from "@shared/utils/themeColors";
import { getProfileQueryKey } from "../../../shared/hooks/useProfile";
import { SettingsService } from "../services/settings.service";
import { DigestFrequency, UpdateProfileDto } from "../types/settings.types";

// Reusable notification option component
interface NotificationOptionProps {
  id: string;
  title: string;
  description: string;
  checked?: boolean;
  onChange: (checked: boolean) => void;
}

function NotificationOption({
  id,
  title,
  description,
  checked,
  onChange,
}: NotificationOptionProps) {
  return (
    <div className="flex justify-between items-center py-2">
      {/* Left */}
      <div className="flex-grow">
        <div className={`${theme.text.primary} font-medium`}>{title}</div>
        <div className={`text-xs ${theme.text.muted}`}>{description}</div>
      </div>
      {/* Right */}
      <div className="flex items-center ml-4">
        <div className={`text-sm text-gray-400 dark:${theme.text.muted} mr-2`}>
          {checked ? "On" : "Off"}
        </div>
        <div className="form-switch">
          <input
            type="checkbox"
            id={id}
            className={`sr-only`}
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
          />
          <label
            className={`bg-gray-400 dark:bg-gray-700 ${theme.form.checkbox}`}
            htmlFor={id}
          >
            <span className="bg-white shadow-sm" aria-hidden="true"></span>
            <span className="sr-only">Enable {title} notifications</span>
          </label>
        </div>
      </div>
    </div>
  );
}

const DIGEST_FREQUENCY_OPTIONS: { value: DigestFrequency; label: string; description: string }[] = [
  {
    value: DigestFrequency.DAILY,
    label: 'Daily',
    description: 'Receive a digest every day with the latest activity',
  },
  {
    value: DigestFrequency.WEEKLY,
    label: 'Weekly',
    description: 'Receive a digest every Monday with the week\'s highlights',
  },
  {
    value: DigestFrequency.MONTHLY,
    label: 'Monthly',
    description: 'Receive a digest on the 1st of each month with a monthly summary',
  },
];

interface NotificationsPanelProps {
  user: User;
}

function NotificationsPanel({ user }: NotificationsPanelProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<UpdateProfileDto>(() => {
    const defaultNotificationSettings = {
      comments: true,
      messages: true,
      mentions: true,
      likePosts: true,
      connections: true,
      newResource: true,
      newSpace: true,
      newEvent: true,
      eventAttendance: true,
    };

    return {
      profile: user.profile,
      settings: {
        ...user.settings,
        digestFrequency: user?.settings?.digestFrequency ?? DigestFrequency.DAILY,
        notificationSettings:
          user?.settings?.notificationSettings || defaultNotificationSettings,
      },
    };
  });

  // Update profile mutation
  const { mutate: updateProfile, isPending: isUpdating } = useMutation({
    mutationFn: (data: UpdateProfileDto) => SettingsService.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getProfileQueryKey() });
      toast.success("Notifications settings updated successfully");
    },
    onError: () => {
      toast.error("Error updating notifications settings");
    },
  });

  const handleChange = (name: string, checked: boolean) => {
    setFormData((prev) => {
      const notificationType = name.replace("notification", "");
      return {
        ...prev,
        settings: {
          ...prev.settings,
          notificationSettings: {
            ...prev.settings.notificationSettings,
            [notificationType]: checked,
          },
        },
      };
    });
  };

  const handleFrequencyChange = (frequency: DigestFrequency) => {
    setFormData((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        digestFrequency: frequency,
      },
    }));
  };

  const handleSave = () => {
    updateProfile(formData);
  };

  return (
    <div className="grow">
      {/* Panel body */}
      <div className="p-6 space-y-6">
        <h2 className={`text-2xl ${theme.text.primary} font-bold mb-5`}>
          My Notifications
        </h2>

        {/* Digest Frequency */}
        <section>
          <h3 className={`text-xl leading-snug ${theme.text.primary} font-bold mb-1`}>
            Digest Email Frequency
          </h3>
          <p className={`text-sm ${theme.text.muted} mb-4`}>
            Choose how often you want to receive your activity digest email.
          </p>
          <ul className="space-y-2">
            {DIGEST_FREQUENCY_OPTIONS.map((option) => {
              const isSelected = formData.settings?.digestFrequency === option.value;
              return (
                <li key={option.value}>
                  <button
                    type="button"
                    onClick={() => handleFrequencyChange(option.value)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors text-left ${isSelected
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : `${theme.border.primary} hover:bg-gray-50 dark:hover:bg-gray-800`
                      }`}
                  >
                    <div
                      className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-blue-500' : 'border-gray-400'
                        }`}
                    >
                      {isSelected && (
                        <div className="w-2 h-2 rounded-full bg-blue-500" />
                      )}
                    </div>
                    <div className="flex-grow">
                      <div className={`${theme.text.primary} font-medium text-sm`}>
                        {option.label}
                      </div>
                      <div className={`text-xs ${theme.text.muted}`}>
                        {option.description}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>

        {/* General */}
        <section>
          <h3
            className={`text-xl leading-snug ${theme.text.primary} font-bold mb-1`}
          >
            General
          </h3>
          {/* Notification Option Component */}
          {/* This component eliminates repetition in the UI */}
          <ul>
            {/* Category: Content Interactions */}
            <li className={`py-3 border-b ${theme.border.primary}`}>
              <div className={`${theme.text.primary} font-semibold mb-2`}>
                Content Interactions
              </div>

              {/* Comments and replies */}
              <NotificationOption
                id="comments"
                title="Comments and replies"
                description="Receive notifications when someone comments on your content"
                checked={formData.settings.notificationSettings?.comments}
                onChange={(checked) => handleChange("comments", checked)}
              />

              {/* Like Posts */}
              <NotificationOption
                id="likePosts"
                title="Like Posts"
                description="Receive notifications when someone likes your posts"
                checked={formData.settings.notificationSettings?.likePosts}
                onChange={(checked) => handleChange("likePosts", checked)}
              />

              {/* Mentions */}
              <NotificationOption
                id="mentions"
                title="Mentions"
                description="Receive notifications when someone mentions you"
                checked={formData.settings.notificationSettings?.mentions}
                onChange={(checked) => handleChange("mentions", checked)}
              />

              {/* Resources */}
              <NotificationOption
                id="newResource"
                title="New Resource"
                description="Receive notifications when a new resource is added"
                checked={formData.settings.notificationSettings?.newResource}
                onChange={(checked) => handleChange("newResource", checked)}
              />

              {/* New Space */}
              <NotificationOption
                id="newSpace"
                title="New Space"
                description="Receive notifications when a new space is added"
                checked={formData.settings.notificationSettings?.newSpace}
                onChange={(checked) => handleChange("newSpace", checked)}
              />

              {/* New Event */}
              <NotificationOption
                id="newEvent"
                title="New Event"
                description="Receive notifications when a new event is added"
                checked={formData.settings.notificationSettings?.newEvent}
                onChange={(checked) => handleChange("newEvent", checked)}
              />

              {/* Event Attendance */}
              <NotificationOption
                id="eventAttendance"
                title="Event Attendance"
                description="Receive notifications when you attend an event"
                checked={
                  formData.settings.notificationSettings?.eventAttendance
                }
                onChange={(checked) => handleChange("eventAttendance", checked)}
              />
            </li>

            {/* Category: Direct Communications */}
            <li className={`py-3 border-b ${theme.border.primary}`}>
              <div className={`${theme.text.primary} font-semibold mb-2`}>
                Direct Communications
              </div>

              {/* Messages */}
              <NotificationOption
                id="messages"
                title="Messages"
                description="Receive notifications for new direct messages"
                checked={formData.settings.notificationSettings?.messages}
                onChange={(checked) => handleChange("messages", checked)}
              />

              {/* Connections */}
              <NotificationOption
                id="connections"
                title="Connections"
                description="Receive notifications about new connection requests"
                checked={formData.settings.notificationSettings?.connections}
                onChange={(checked) => handleChange("connections", checked)}
              />
            </li>
          </ul>
        </section>
      </div>

      {/* Panel footer */}
      <footer>
        <div
          className={`flex flex-col px-6 py-5 border-t ${theme.border.primary}`}
        >
          <div className="flex self-end">
            <Button onClick={handleSave} type="button" disabled={isUpdating}>
              {isUpdating ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default NotificationsPanel;
